
import React, { useRef, useCallback } from 'react';
import { FileAudio, FileVideo } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';

interface FileUploadTabsProps {
  onFileSelect: (file: File) => void;
}

export const FileUploadTabs = ({ onFileSelect }: FileUploadTabsProps) => {
  const { t } = useTranslation();
  const audioInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      console.log('📄 File selected from input:', file.name, file.type);
      onFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  }, [onFileSelect]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    const fileType = file.type;
    if (fileType.startsWith("audio/") || fileType.startsWith("video/")) {
      console.log('📄 File dropped:', file.name, file.type);
      onFileSelect(file);
    }
  }, [onFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleAudioFileSelect = useCallback(() => {
    console.log('📄 Audio file select button clicked');
    audioInputRef.current?.click();
  }, []);

  const handleVideoFileSelect = useCallback(() => {
    console.log('📄 Video file select button clicked');
    videoInputRef.current?.click();
  }, []);

  const DropZone = ({ 
    icon: Icon, 
    title, 
    onClick, 
    inputRef, 
    accept, 
    supportedFormats 
  }: {
    icon: React.ComponentType<any>;
    title: string;
    onClick: () => void;
    inputRef: React.RefObject<HTMLInputElement>;
    accept: string;
    supportedFormats: string;
  }) => (
    <div className="space-y-4 bg-white">
      <div
        className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 hover:bg-gray-50 transition-colors"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragOver}
      >
        <Icon className="h-10 w-10 text-gray-400 mb-4" />
        <p className="text-sm font-medium text-gray-900 mb-1">
          {title}
        </p>
        <p className="text-xs text-gray-500 mb-4">
          {t('or_click_to_select')}
        </p>
        <Button
          variant="secondary"
          size="sm"
          className="bg-gray-100 text-gray-800 hover:bg-gray-200 hover:text-gray-900"
          type="button"
          onClick={onClick}
        >
          {t('select_file')}
        </Button>
      </div>
      <div className="text-xs text-gray-500 text-center">
        {supportedFormats}
      </div>
      <Input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );

  return (
    <Tabs defaultValue="audio" className="w-full">
      <TabsList className="grid w-full grid-cols-2 bg-gray-100">
        <TabsTrigger 
          value="audio" 
          className="data-[state=active]:bg-white data-[state=active]:text-gray-900"
        >
          {t('audio')}
        </TabsTrigger>
        <TabsTrigger 
          value="video" 
          className="data-[state=active]:bg-white data-[state=active]:text-gray-900"
        >
          {t('video')}
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="audio">
        <DropZone
          icon={FileAudio}
          title={t('drag_drop_audio')}
          onClick={handleAudioFileSelect}
          inputRef={audioInputRef}
          accept="audio/*"
          supportedFormats={t('supported_audio_formats')}
        />
      </TabsContent>
      
      <TabsContent value="video">
        <DropZone
          icon={FileVideo}
          title={t('drag_drop_video')}
          onClick={handleVideoFileSelect}
          inputRef={videoInputRef}
          accept="video/*"
          supportedFormats={t('supported_video_formats')}
        />
      </TabsContent>
    </Tabs>
  );
};
